import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { components, DropdownIndicatorProps, MultiValue, SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { Text } from '@connect-x-app/hybrid-ui';
import { Option } from '@connect-x-app/shared-types';
import { COLORS } from '@connect-x-app/shared-ui';
import { INPUT_TEST_IDS } from '@connect-x-app/web-test-ids';

import { Icon } from '../../icon';
import { ClearIndicator } from '../clearIndicator/ClearIndicator';
import { useInputError } from '../hooks/useInputError';
import { InputWrapper, InputWrapperProps } from '../InputWrapper';
import { MultiValueRemove } from '../multiValueRemove/MultiValueRemove';

import './typeAhead.scss';

type TypeAheadProps = InputWrapperProps & {
  loadOptions: (search: string) => Promise<Option[]>;
  onChange: (newValue: readonly Option[], name: string) => void;
  placeholder: string;
  selectedOptions: readonly Option[];
};

const DropdownIndicator = ({ ...props }: DropdownIndicatorProps<Option, true>) => (
  <components.DropdownIndicator {...props}>
    {props.children}
    <Icon
      colorName={COLORS.zulu.$08}
      name={props.selectProps.menuIsOpen ? 'chevronUp' : 'chevronDown'}
      testId={props.selectProps.menuIsOpen ? INPUT_TEST_IDS.select.chevronUp : INPUT_TEST_IDS.select.chevronDown}
    />
  </components.DropdownIndicator>
);

export const TypeAhead = ({
  label,
  loadOptions: fetchOptions,
  onChange,
  placeholder,
  selectedOptions,
  ...wrapperProps
}: TypeAheadProps) => {
  const { t } = useTranslation();
  const { name, validation } = wrapperProps;
  const { setDirty, showError } = useInputError(validation);
  const debounceTimeout = useRef<number>(null);
  const [defaultOptions, setDefaultOptions] = useState<Option[]>();

  const loadOptions = (search: string) =>
    new Promise<Option[]>(resolve => {
      if (search) {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = window.setTimeout(async () => {
          try {
            const options = await fetchOptions(search);
            resolve(options);
          } catch {
            resolve([]);
          }
        }, 300);
      } else {
        resolve(defaultOptions ?? []);
      }
    });

  const onChangeInternal = (newValue: SingleValue<Option> | MultiValue<Option>) => {
    if (newValue) {
      onChange(newValue as MultiValue<Option>, name);
    } else {
      onChange([], name);
    }
  };

  const onFocus = async () => {
    if (!defaultOptions) {
      try {
        const options = await fetchOptions('');
        setDefaultOptions(options);
      } catch {
        setDefaultOptions([]);
      }
    }
  };

  return (
    <InputWrapper {...wrapperProps} showError={showError}>
      <Text as="span" className="type-ahead__label" color={COLORS.zulu.$06} size="XS" weight="bold">
        {label}
      </Text>
      <AsyncSelect
        cacheOptions={true}
        className={'type-ahead'}
        classNamePrefix="type-ahead"
        closeMenuOnSelect={false}
        components={{
          ClearIndicator,
          DropdownIndicator,
          MultiValueRemove,
        }}
        defaultOptions={defaultOptions}
        hideSelectedOptions={false}
        isMulti
        loadOptions={loadOptions}
        loadingMessage={() => t('TYPEAHEAD.LOADING')}
        noOptionsMessage={({ inputValue }) => (inputValue ? t('TYPEAHEAD.NO_OPTIONS') : t('TYPEAHEAD.TYPE_TO_SEARCH'))}
        onChange={(newValue: SingleValue<Option> | MultiValue<Option>) => {
          onChangeInternal(newValue);
          setDirty();
        }}
        onFocus={onFocus}
        placeholder={placeholder}
        value={selectedOptions}
      />
    </InputWrapper>
  );
};
